import { CommitableSlidePanel } from "@/components/slide-panel";
import { Component, Mixins, Prop } from "vue-property-decorator";
import BeanExportPdf from '@/lib/models/bean-export-pdf';
import CavCavite from '@/lib/models/cav-cavite';
import CavAbstractDocument from '@/lib/models/cav-abstract-document';
import { exportPdf, FILE_PROXY_URL } from '@/lib/services';
import Icon from '@/components/icon';


@Component
export default class PanelExportPdf extends Mixins(CommitableSlidePanel) {

  @Prop({required: true, type: Object}) readonly cavite!: CavCavite;

  protected data: BeanExportPdf;
  protected dlLink: string = null;
  protected submitted: boolean = false;
  
  created() {
    this.data = new BeanExportPdf().inject({
      documents: this.cavite.documents.map(d => d.id),
      photos: this.cavite.photos.map(d => d.id),
      scans: this.cavite.scans.map(d => d.id)
    });
  }

  get rootClasses() {
    return ["panel-export-pdf"];
  }

  get isCommitable() {
    return true;
  }

  get isPoc() {
    return this.cavite.isInPoc;
  }

  async doExport(bean: BeanExportPdf | null): Promise<string> {
    const pdfFile = await exportPdf({
      numCavite: this.cavite.numCavite,
      bean
    });
    return `${FILE_PROXY_URL}/fiche/${pdfFile}`;
  }

  async onClickSubmit() {
    if (this.isCommitable) {
      this.submitted = true;
      this.dlLink = await this.doExport(this.data);
    }
  }

  onClickCommit() {
    this.commit({data: null});
  }

  onClickCancel() {
    this.cancel();
  }

  async mounted() {
    if (!this.isPoc) {
      this.dlLink = await this.doExport(null);
    }
  }

  renderHead() {
    return <span>Export la fiche en PDF</span>
  }

  renderCbx(title: string, val: boolean, onInput: (v) => void) {
    return (
      <div class="field is-horizontal">
        <label class="checkbox label is-small">
          <input class="checkbox" type="checkbox" checked={val} onInput={onInput}/>
          {title}
        </label>
      </div>
    );
  }

  renderCbxDocs(title: string, arr: CavAbstractDocument[], selecteds: number[]) {
    return (
      <div class="field">
        <label class="label is-small">{title}</label>
        <div class="control sub-control">
          {arr.map(
            v => this.renderCbx(
              v.titre,
              Boolean(~selecteds.indexOf(v.id)),
              v => {
                selecteds = selecteds.filter(id => id !== v.id);
                v && selecteds.push(v.id);
              }
            )
          )}
        </div>
      </div>
    );
  }

  renderBeanConfiguration() {
    return (
      <div class="column">
        {this.renderCbx("Inclure le plan", this.data.includeMap, v => this.data.includeMap = v)}
        {this.cavite.documents.length > 0 && this.renderCbxDocs("Inclure les documents :", this.cavite.documents, this.data.documents)}
        {this.cavite.scans.length > 0 && this.renderCbxDocs("Inclure les scans :", this.cavite.scans, this.data.scans)}
        {this.cavite.photos.length > 0 && this.renderCbxDocs("Inclure les photos :", this.cavite.scans, this.data.scans)}
        {this.cavite.proprietaires.length > 0 && this.renderCbx("Inclure les proprietaires", this.data.includeProprietaires, v => this.data.includeProprietaires = v)}
        {this.cavite.refCadastrales.length > 0 && this.renderCbx("Inclure les ref. cadastrales", this.data.includeRefCadastrales, v => this.data.includeRefCadastrales = v)}
        {this.cavite.accesPossibles.length > 0 && this.renderCbx("Inclure les accès possibles", this.data.includeAccesPossibles, v => this.data.includeAccesPossibles = v)}
        {this.cavite.piliers.length > 0 && this.renderCbx("Inclure les piliers", this.data.includePiliers, v => this.data.includePiliers = v)}
        {this.cavite.tauxDefruitements.length > 0 && this.renderCbx("Inclure les taux de defruitements", this.data.includeTauxDefruitements, v => this.data.includeTauxDefruitements = v)}
        {this.cavite.descriptionSommaires.length > 0 && this.renderCbx("Inclure les descriptions sommaires", this.data.includeDescriptionSommaires, v => this.data.includeDescriptionSommaires = v)}
        {this.cavite.enjeuxSurfaces.length > 0 && this.renderCbx("Inclure les enjeux en surface", this.data.includeEnjeuxSurfaces, v => this.data.includeEnjeuxSurfaces = v)}
        {this.cavite.observationChiropteres.length > 0 && this.renderCbx("Inclure les observations de chiropteres", this.data.includeObservationChiropteres, v => this.data.includeObservationChiropteres = v)}
        {this.cavite.evenementObservations.length > 0 && this.renderCbx("Inclure les evenements/observations", this.data.includeEvenementObservations, v => this.data.includeEvenementObservations = v)}
        {this.cavite.suivis.length > 0 && this.renderCbx("Inclure les suivis", this.data.includeSuivis, v => this.data.includeSuivis = v)}
        {this.cavite.preconisations.length > 0 && this.renderCbx("Inclure les préconisations", this.data.includePreconisations, v => this.data.includePreconisations = v)}
      </div>
    );
  }

  renderBody() {
    return (
      <div class="basic">
        <div class="columns">
          {this.dlLink ? (
            <div class="column has-text-centered">
              <a class="button" href={this.dlLink} target="_blank">
                <Icon icon="download" class="icon is-large"/>
                <span>Télécharger le PDF généré</span>
              </a>
            </div>
          ) : (
            this.isPoc && !this.submitted ? this.renderBeanConfiguration() : <div class={`column async is-loading is-large`} style="height: 100px"></div>
          )}
        </div>
      </div>
    );
  }

  renderFooter() {
    return (
      <div class="columns is-fluid">
        <div class="column is-7"></div>
        <div class="column is-5 is-flex justify-end">
          <button class="button is-small is-fluid" onClick={this.onClickCancel}>{this.dlLink ? 'Fermer' : 'Annuler'}</button>
          {this.isPoc && !this.submitted && this.dlLink == null && (
            <button class={`button is-primary is-small is-fluid`}
            onClick={this.onClickSubmit}>
              Exporter
            </button>
          )}
        </div>
      </div>
    )
  }
  
}
// VUE JSX HOT LOADER //
if (module.hot) require("/Users/home/j2ee/projects/cavites/src/main/client/node_modules/vue-jsx-hot-loader/src/api.js")({ Vue: require('vue'), ctx: eval('this'), module: module, hotId: "_vue_jsx_hot-3cc7b844/panel-export-pdf.tsx" });